import { percentageFromDecimal } from '@sequencehq/utils'
import type { Dashboardv99990101CommonModels } from '@sequencehq/api/dashboard/v99990101'

import {
  FormValues,
  SubmitValues
} from 'CreditNotes/components/drawers/BaseCreditNoteLineItemDrawer'

import { CreditNoteLineItemRow } from './CreditNoteLineItemTable'
import { lineItemNet } from './lineItemNet'

type CreditNoteLineItemRateDisplay =
  Dashboardv99990101CommonModels.CreditNoteLineItems.CreditNoteLineItemRateDisplay

/**
 * Row fields derived from the line item drawer form. Shared by the add and edit
 * flows so both map drawer output to a CreditNoteLineItemRow identically.
 */
type CreditNoteLineItemRowFormFields = Pick<
  CreditNoteLineItemRow,
  | 'title'
  | 'productId'
  | 'rate'
  | 'taxRate'
  | 'qty'
  | 'price'
  | 'description'
  | 'rateDisplay'
  | 'servicePeriod'
  | 'revenueRecognitionMethod'
>

export const submitValuesToRowFields = (
  values: SubmitValues
): CreditNoteLineItemRowFormFields => ({
  title: values.title,
  productId: values.productId,
  rate: values.rate,
  taxRate: values.taxRate,
  qty: values.quantity,
  price: lineItemNet(values.rate, values.quantity),
  description: values.description || undefined,
  rateDisplay: values.rateDisplay,
  servicePeriod: values.servicePeriod,
  revenueRecognitionMethod: values.revenueRecognitionMethod
})

/**
 * Maps an existing row back to drawer form values for the edit flow. Mirrors
 * CreditNotes/components/drawers/EditCreditNoteLineItemDrawer.tsx: the form's
 * amount holds the rate for fixed line items and the percentage for percentage
 * ones, and tax is a percentage rather than the stored decimal.
 */
export const rowToFormValues = (row: CreditNoteLineItemRow): FormValues => {
  const rateDisplay =
    row.rateDisplay ?? ('ABSOLUTE' as CreditNoteLineItemRateDisplay)

  return {
    title: row.title,
    description: row.description ?? '',
    rateDisplay,
    quantity: row.qty,
    amount:
      rateDisplay === 'ABSOLUTE' ? row.rate : percentageFromDecimal(row.rate),
    tax: percentageFromDecimal(row.taxRate),
    productId: row.productId,
    taxCategoryId: '',
    servicePeriod: row.servicePeriod,
    revenueRecognitionMethod: row.revenueRecognitionMethod
  }
}
